import { defineStore } from 'pinia';
import useCommonStore from '@/common/store/pinia/common';
import useS3ServiceStore from '@/common/store/pinia/s3Service';
import AwsS3Client from '@/common/utils/s3Client';
import api from '@/common/utils/api';
import useAuthStore from '@/common/store/pinia/auth';
import type { ProjectS3BucketList } from '@schema';
import { DEFAULT_REQUEST_VIEW } from '@/common/store/modules/common/common.constants';
import { ref, set } from 'vue';
import threadRequests from '@/common/utils/threadRequests';

type RawBucket = {
  name: string
  creationDate: Date
  path?: string[]
  userId?: string
  arn?: string
}

const mapItem = (bucket: RawBucket) => ({
  id: bucket.name,
  ...bucket
});

export type S3Bucket = ReturnType<typeof mapItem>;

export default defineStore('s3Buckets', () => {
  const commonStore = useCommonStore<RawBucket, S3Bucket>({
    getUrl: () => '',
    mapItem,
    itemKey: 'name'
  })();

  const {
    viewLists,
    getItems,
    removeItem,
    addViewList,
    setData,
    getItemById
  } = commonStore;

  const isS3Available = ref<boolean | null>(null);
  const endpoints = ref<string[]>([]);

  const fetchBuckets = async () => {
    const viewList = addViewList(DEFAULT_REQUEST_VIEW);
    viewList.error = null;
    viewList.loading = true;

    const s3Service = useS3ServiceStore();

    if (s3Service.isLegacyS3User) {
      isS3Available.value = true;
      try {
        const { Buckets } = await AwsS3Client.listBuckets();

        endpoints.value = s3Service.config?.endpoints || [];

        const rawItems = (Buckets || []).map<RawBucket>(bucket => ({
          name: bucket.Name || 'unknown',
          creationDate: bucket.CreationDate || new Date(),
          path: endpoints.value.map(endpoint => `${endpoint}/${bucket.Name}`)
        }));

        setData(rawItems, DEFAULT_REQUEST_VIEW);
        viewLists.value[DEFAULT_REQUEST_VIEW].count = rawItems.length;
        return { data: Buckets };
      } catch (error) {
        set(viewList, 'list', []);
        viewList.error = error as Error;
        throw error;
      } finally {
        viewList.loading = false;
      }
    }

    const authStore = useAuthStore();
    const domainId = authStore.currentUser?.domain_id || '';
    const projectId = authStore.currentUserProjectId;

    try {
      const [bucketsResponse, projectEndpoints] = await Promise.all([
        api.get<ProjectS3BucketList>(`/api/v2/domains/${domainId}/projects/${projectId}/s3/buckets/`),
        s3Service.getProjectS3Status(domainId, projectId).then(status => status?.endpoints || [], () => [])
      ]);
      isS3Available.value = true;
      endpoints.value = projectEndpoints;

      const rawItems = (bucketsResponse.data.data || []).map<RawBucket>(bucket => ({
        name: bucket.name || 'unknown',
        creationDate: bucket.creation_date ? new Date(bucket.creation_date) : new Date(),
        path: endpoints.value.map(endpoint => `${endpoint}/${bucket.name}`),
        userId: bucket.user_id,
        arn: bucket.arn as string | undefined
      }));

      setData(rawItems, DEFAULT_REQUEST_VIEW);
      viewLists.value[DEFAULT_REQUEST_VIEW].count = bucketsResponse.data.count ?? rawItems.length;
      return bucketsResponse.data;
    } catch (error) {
      isS3Available.value = false;
      set(viewList, 'list', []);
      viewList.error = error as Error;
      return null;
    } finally {
      viewList.loading = false;
    }
  };

  const createBucket = async (name: string) => {
    await AwsS3Client.createBucket({ Bucket: name });
    return fetchBuckets();
  };

  const deleteBuckets = (bucketsItem: S3Bucket[]) => {
    const tasks = bucketsItem.map((bucket) => () => AwsS3Client.deleteBucket({ Bucket: bucket.id }).then(() => removeItem(bucket.id)));
    return threadRequests(tasks);
  };

  return {
    getItems,
    viewLists,
    isS3Available,
    endpoints,
    fetchBuckets,
    createBucket,
    deleteBuckets,
    getItemById,
  };
});
